import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Tooltip } from "@mui/material";
import { LanguageCode } from "@bto/shared";
import { DownloadIcon } from "../../../assets/iconComponents/DownloadIcon";
import {
  DropdownContainer,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "./DownloadDropdown.styles";

const HOVER_CLOSE_DELAY_MS = 120;

export interface DownloadLanguageItem {
  language: LanguageCode;
  label: string;
  isDownloading: boolean;
  onDownload: () => void;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

interface DownloadDropdownProps {
  items: DownloadLanguageItem[];
  isRead: boolean;
  downloaded: boolean;
}

export const DownloadDropdown: React.FC<DownloadDropdownProps> = ({
  items,
  isRead,
  downloaded,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iconColor = isRead || downloaded ? "lightgray" : undefined;

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 220;
    const menuHeight = menuRef.current?.offsetHeight ?? 100;
    const MARGIN = 8;
    // Clamp left so the menu doesn't overflow the right edge of the viewport
    const left = Math.min(rect.left, window.innerWidth - menuWidth - MARGIN);
    // Flip above the trigger when there isn't enough room below
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const top =
      spaceBelow >= menuHeight
        ? rect.bottom + MARGIN
        : rect.top - menuHeight - MARGIN;
    setMenuPosition({ top, left });
  }, []);

  // Clear any pending close timeout on unmount to avoid state updates on an unmounted component
  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    [],
  );

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", computePosition, true);
    window.addEventListener("resize", computePosition);
    return () => {
      window.removeEventListener("scroll", computePosition, true);
      window.removeEventListener("resize", computePosition);
    };
  }, [isOpen, computePosition]);

  // Close on Escape or click outside (trigger + portaled menu are both checked)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insideMenu = menuRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insideMenu) setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen]);

  const openMenu = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    computePosition();
    setIsOpen(true);
  }, [computePosition]);

  const scheduleClose = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  }, []);

  const cancelClose = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isOpen) {
      setIsOpen(false);
    } else {
      openMenu();
    }
  };

  const handleItemClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, onDownload: () => void) => {
      e.stopPropagation();
      e.preventDefault();
      setIsOpen(false);
      onDownload();
    },
    [],
  );

  return (
    <DropdownContainer
      ref={containerRef}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <DropdownTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Download report"
        onClick={handleTriggerClick}
      >
        <DownloadIcon color={iconColor} />
      </DropdownTrigger>
      {ReactDOM.createPortal(
        <DropdownMenu
          ref={menuRef}
          $isOpen={isOpen}
          $top={menuPosition.top}
          $left={menuPosition.left}
          role="menu"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {items.map((item) => {
            const isDisabled = item.isDownloading || item.isDisabled;
            const label = item.isDownloading ? "Downloading..." : item.label;
            const button = (
              <DropdownItem
                role="menuitem"
                onClick={
                  item.isDisabled
                    ? undefined
                    : (e) => handleItemClick(e, item.onDownload)
                }
                disabled={isDisabled}
              >
                {label}
              </DropdownItem>
            );
            if (item.isDisabled && item.disabledTooltip) {
              return (
                <Tooltip
                  key={item.language}
                  title={item.disabledTooltip}
                  arrow
                  placement="left"
                >
                  {/* span wrapper lets the Tooltip fire on a disabled child */}
                  <span style={{ display: "block" }}>{button}</span>
                </Tooltip>
              );
            }
            return (
              <React.Fragment key={item.language}>{button}</React.Fragment>
            );
          })}
        </DropdownMenu>,
        document.body,
      )}
    </DropdownContainer>
  );
};
